import { buildDirectorsSignersJson } from '../../allotment-shares/composables/allotmentSigners';
import { dedupePresetSigners } from '../../presetSignerDedup';

function trim(v: unknown): string {
  return v === null || v === undefined ? '' : String(v).trim();
}

export function isAppointDirectorDrDocument(templateName: string): boolean {
  return /DR\s*\(\s*Appoint\s+Director/i.test(String(templateName || ''));
}

export function isAppointSecretaryDrDocument(templateName: string): boolean {
  return /DR\s*\(\s*Appoint\s+Secretary/i.test(String(templateName || ''));
}

export function isOfficerAppointmentDrDocument(templateName: string): boolean {
  return isAppointDirectorDrDocument(templateName) || isAppointSecretaryDrDocument(templateName);
}

/** Directors' Resolution (Appoint Director).docx */
export function isDirectorsResolutionAppointDirectorDocument(templateName: string): boolean {
  return /Directors[''\u2019]?\s*Resolution\s*\(\s*Appoint\s+Director/i.test(
    String(templateName || ''),
  );
}

/** Directors' Resolution (Appoint Secretary).docx */
export function isDirectorsResolutionAppointSecretaryDocument(templateName: string): boolean {
  return /Directors[''\u2019]?\s*Resolution\s*\(\s*Appoint\s+Secretary/i.test(
    String(templateName || ''),
  );
}

/** Appoint Director — 엔티티 현직 디렉터 전원 자동 사이너 대상 */
export function isAppointDirectorEntityDirectorsSignerDocument(templateName: string): boolean {
  return (
    isAppointDirectorDrDocument(templateName) ||
    isDirectorsResolutionAppointDirectorDocument(templateName)
  );
}

/** Appoint Secretary — 엔티티 현직 디렉터 전원 자동 사이너 대상 */
export function isAppointSecretaryEntityDirectorsSignerDocument(templateName: string): boolean {
  return (
    isAppointSecretaryDrDocument(templateName) ||
    isDirectorsResolutionAppointSecretaryDocument(templateName)
  );
}

/** Appoint Director / Secretary — DR·Directors' Resolution 공통 */
export function isAppointOfficerEntityDirectorsSignerDocument(templateName: string): boolean {
  return (
    isAppointDirectorEntityDirectorsSignerDocument(templateName) ||
    isAppointSecretaryEntityDirectorsSignerDocument(templateName)
  );
}

export function isWithdrawDirectorPresetDocument(templateName: string): boolean {
  const fn = String(templateName || '');
  return (
    /DR\s*\(\s*Withdraw\s+Director/i.test(fn)
    || /Resignation\s+Letter\s*\(\s*Director/i.test(fn)
  );
}

export function isWithdrawSecretaryPresetDocument(templateName: string): boolean {
  const fn = String(templateName || '');
  return (
    /DR\s*\(\s*Withdraw\s+Secretary/i.test(fn)
    || /Resignation\s+Letter\s*\(\s*Secretary/i.test(fn)
  );
}

export function isOfficerWithdrawPresetDocument(templateName: string): boolean {
  return isWithdrawDirectorPresetDocument(templateName) || isWithdrawSecretaryPresetDocument(templateName);
}

function hasPresetSignerJson(formData: Record<string, any>): boolean {
  const raw = formData.signers ?? formData.Signers;
  if (raw == null || String(raw).trim() === '') return false;
  try {
    const parsed = typeof raw === 'string' ? JSON.parse(raw) : raw;
    return Array.isArray(parsed) && parsed.some((s) => trim(s?.name));
  } catch {
    return false;
  }
}

/** 화면에서 지정한 선임/해임 대상 1명 — DR·Resignation Letter 자동 사이너 */
export function buildAppointedOfficerSignerJson(input: {
  name: string;
  email?: string;
  role?: string;
  contactCd?: string;
  contactRegNo?: string;
}): string {
  const name = trim(input.name);
  if (!name) return '';
  const list = dedupePresetSigners([
    {
      name,
      email: trim(input.email),
      role: trim(input.role) || 'signer',
      contactCd: trim(input.contactCd),
      contactRegNo: trim(input.contactRegNo),
    },
  ]);
  return list.length ? JSON.stringify(list) : '';
}

export function buildAppointDirectorSignerJsonFromFormData(
  formData: Record<string, any> | null | undefined,
): string {
  if (!formData || typeof formData !== 'object') return '';
  const name =
    trim(formData.director_name) ||
    trim(formData.directorName) ||
    trim(formData.officerName) ||
    trim(formData.CONTACT_NAME);
  const email = trim(formData.director_email) || trim(formData.directorEmail);
  const contactCd = trim(formData.contactCd) || trim(formData.officerNameCd);
  const contactRegNo =
    trim(formData.director_identification_no) ||
    trim(formData.directorIdentificationNo) ||
    trim(formData.CONTACT_REG_NO);
  return buildAppointedOfficerSignerJson({
    name,
    email,
    role: 'director',
    contactCd,
    contactRegNo,
  });
}

export function buildAppointSecretarySignerJsonFromFormData(
  formData: Record<string, any> | null | undefined,
): string {
  if (!formData || typeof formData !== 'object') return '';
  const name =
    trim(formData.secretary_name) ||
    trim(formData.secretaryName) ||
    trim(formData.officerName);
  const email =
    trim(formData.secretary_email) ||
    trim(formData.secretaryEmail) ||
    trim(formData.contactData?.contactEmail) ||
    trim(formData.contactData?.CONTACT_EMAIL);
  return buildAppointedOfficerSignerJson({
    name,
    email,
    role: 'secretary',
    contactCd: trim(formData.contactCd) || trim(formData.officerNameCd),
    contactRegNo: trim(formData.secretary_identification_no) || trim(formData.secretaryIdentificationNo),
  });
}

export function buildWithdrawDirectorSignerJsonFromFormData(
  formData: Record<string, any> | null | undefined,
): string {
  if (!formData || typeof formData !== 'object') return '';
  const name =
    trim(formData.resign_director_name) ||
    trim(formData.resignDirectorName) ||
    trim(formData.officerName) ||
    trim(formData.CONTACT_NAME);
  const email =
    trim(formData.director_email) ||
    trim(formData.directorEmail) ||
    trim(formData.contactData?.contactEmail) ||
    trim(formData.contactData?.CONTACT_EMAIL);
  return buildAppointedOfficerSignerJson({
    name,
    email,
    role: 'director',
    contactCd: trim(formData.contactCd) || trim(formData.officerNameCd),
    contactRegNo:
      trim(formData.director_identification_no) ||
      trim(formData.directorIdentificationNo) ||
      trim(formData.CONTACT_REG_NO),
  });
}

export function buildWithdrawSecretarySignerJsonFromFormData(
  formData: Record<string, any> | null | undefined,
): string {
  if (!formData || typeof formData !== 'object') return '';
  const name =
    trim(formData.secretary_name) ||
    trim(formData.secretaryName) ||
    trim(formData.officerName);
  const email =
    trim(formData.secretary_email) ||
    trim(formData.secretaryEmail) ||
    trim(formData.contactData?.contactEmail) ||
    trim(formData.contactData?.CONTACT_EMAIL);
  return buildAppointedOfficerSignerJson({
    name,
    email,
    role: 'secretary',
    contactCd: trim(formData.contactCd) || trim(formData.officerNameCd),
    contactRegNo:
      trim(formData.secretary_identification_no) ||
      trim(formData.secretaryIdentificationNo),
  });
}

/** EDIT 진입 — Appoint Director/Secretary: 엔티티 디렉터 전원 */
export async function hydrateSgOfficerAppointmentPresetSignersIfEmpty(
  formData: Record<string, any>,
  entityCd: string,
  meta?: { fileName?: string; label?: string },
): Promise<void> {
  if (!formData || typeof formData !== 'object') return;
  const fn = String(meta?.fileName || meta?.label || '');
  if (!isAppointOfficerEntityDirectorsSignerDocument(fn)) return;
  if (hasPresetSignerJson(formData)) return;

  const json = await buildDirectorsSignersJson(trim(entityCd));
  if (!json) return;
  formData.signers = json;
  formData.Signers = json;
}

/** EDIT 진입 시 해임 대상(디렉터/비서)으로 사이너 보충 */
export function hydrateSgOfficerWithdrawPresetSignersIfEmpty(
  formData: Record<string, any>,
  meta?: { fileName?: string; label?: string },
): void {
  if (!formData || typeof formData !== 'object') return;
  const fn = String(meta?.fileName || meta?.label || '');
  if (!isOfficerWithdrawPresetDocument(fn) || hasPresetSignerJson(formData)) return;

  const json = isWithdrawSecretaryPresetDocument(fn)
    ? buildWithdrawSecretarySignerJsonFromFormData(formData)
    : buildWithdrawDirectorSignerJsonFromFormData(formData);
  if (!json) return;
  formData.signers = json;
  formData.Signers = json;
}

/** 선임·해임 SG Officer preset — EDIT 시 사이너 자동 보충 */
export async function hydrateSgOfficerPresetSignersIfEmpty(
  formData: Record<string, any>,
  entityCd: string,
  meta?: { fileName?: string; label?: string },
): Promise<void> {
  await hydrateSgOfficerAppointmentPresetSignersIfEmpty(formData, entityCd, meta);
  hydrateSgOfficerWithdrawPresetSignersIfEmpty(formData, meta);
}
